import React from "react";
import { Box, Stack, Typography, IconButton, Tooltip, Switch } from "@mui/material";
import {
  MdStore,
  MdPhone,
  MdEmail,
  MdLocationOn,
  MdOpenInNew,
  MdInventory2,
  MdEdit,
  MdDelete,
  MdRestoreFromTrash,
} from "react-icons/md";

import { StatusChip } from "components/ui";
import { T, tint } from "theme/tokens";

/**
 * The directory's columns.
 *
 * Extracted from the page because there are a lot of them: this table is the
 * merge of what used to be two screens, so a column is only earning its place
 * if it answers a question ops actually asks. Related fields are grouped into
 * one two-line cell rather than given a column each — twenty single-value
 * columns is a horizontal scroll nobody reads.
 */

const mono = {
  fontFamily: "monospace",
  fontSize: "0.72rem",
  color: T.subtle,
};

/** Two lines in one cell: what it is, and how to find it. */
function TwoLine({ primary, secondary, primarySx, secondarySx }) {
  return (
    <Box sx={{ py: 0.7, minWidth: 0, width: "100%" }}>
      <Typography
        sx={{
          fontSize: "0.83rem",
          fontWeight: 600,
          color: T.ink,
          lineHeight: 1.35,
          ...primarySx,
        }}
      >
        {primary || "—"}
      </Typography>
      {secondary !== undefined && (
        <Typography sx={{ fontSize: "0.72rem", color: T.subtle, ...secondarySx }}>
          {secondary || "—"}
        </Typography>
      )}
    </Box>
  );
}

const BANK_LABELS = {
  verified: "Bank verified",
  pending: "Bank pending",
  failed: "Bank failed",
  unverified: "Bank unverified",
};

const day = (value) => {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? String(value)
    : d.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
};

/**
 * `dateOfRegistration` is a free-form string and the data holds more than one
 * format, so anything unparseable is shown verbatim rather than as "Invalid
 * Date" — the raw value is at least the truth.
 */
const looseDate = (value) => (value ? day(value) : null);

const hours = (businessTiming) => {
  if (!Array.isArray(businessTiming) || !businessTiming.length) return null;
  const [first] = businessTiming;
  if (!first?.start && !first?.end) return null;
  return `${first.start || "?"} – ${first.end || "?"}`;
};

export default function buildColumns({
  onOpenConsole,
  onOpenInventory,
  onEdit,
  onDelete,
  onRestore,
  onToggleVerified,
  busyId,
}) {
  return [
    {
      field: "medstownid",
      headerName: "ID",
      width: 130,
      sortable: false,
      renderCell: ({ row }) => (
        <TwoLine
          primary={row.medstownid || "—"}
          secondary={row.pharmacyId}
          primarySx={{ fontFamily: "monospace", fontSize: "0.78rem", color: T.primary }}
          secondarySx={{ ...mono, fontSize: "0.65rem", wordBreak: "break-all" }}
        />
      ),
    },
    {
      field: "bussinessName",
      headerName: "Pharmacy",
      flex: 1.5,
      minWidth: 230,
      sortable: false,
      renderCell: ({ row }) => (
        <Stack direction="row" spacing={1.2} alignItems="center" sx={{ minWidth: 0, py: 0.7 }}>
          <Box
            sx={{
              width: 30,
              height: 30,
              borderRadius: 1.5,
              flexShrink: 0,
              display: "grid",
              placeItems: "center",
              bgcolor: tint(T.primary, 0.1),
              color: T.primary,
            }}
          >
            <MdStore size={16} />
          </Box>
          <TwoLine
            primary={row.bussinessName || "Unnamed pharmacy"}
            secondary={row.fullName ? `Owner: ${row.fullName}` : "No owner on file"}
          />
        </Stack>
      ),
    },
    {
      field: "businessPhone",
      headerName: "Contact",
      flex: 1,
      minWidth: 190,
      sortable: false,
      renderCell: ({ row }) => (
        <Stack spacing={0.4} sx={{ py: 0.7, minWidth: 0 }}>
          <Stack direction="row" spacing={0.7} alignItems="center">
            <MdPhone size={13} color={T.subtle} />
            <Typography sx={{ fontFamily: "monospace", fontSize: "0.78rem", color: T.ink }}>
              {row.businessPhone || row.deletedBusinessPhone || "—"}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={0.7} alignItems="center" sx={{ minWidth: 0 }}>
            <MdEmail size={13} color={T.subtle} />
            <Typography sx={{ fontSize: "0.72rem", color: T.muted, wordBreak: "break-all" }}>
              {row.email || "—"}
            </Typography>
          </Stack>
        </Stack>
      ),
    },
    {
      field: "address",
      headerName: "Location",
      flex: 1.6,
      minWidth: 230,
      sortable: false,
      renderCell: ({ row }) => (
        <Stack direction="row" spacing={0.7} sx={{ py: 0.7, minWidth: 0 }}>
          <MdLocationOn size={14} color={row.coordinates ? T.secondary : T.subtle} style={{ flexShrink: 0, marginTop: 2 }} />
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: "0.78rem", color: T.ink, lineHeight: 1.35 }}>
              {row.address || "No address"}
            </Typography>
            <Stack direction="row" spacing={0.8} alignItems="center" sx={{ mt: 0.3 }}>
              <Typography sx={{ ...mono }}>
                {row.pincode ? `PIN ${row.pincode}` : "No pincode"}
              </Typography>
              {/* A pin on the map is the difference between a pharmacy
                  customers can be routed to and one they cannot. */}
              {row.coordinates ? (
                <Typography sx={{ fontSize: "0.68rem", color: T.success, fontWeight: 600 }}>
                  Mapped
                </Typography>
              ) : (
                <Typography sx={{ fontSize: "0.68rem", color: T.warning, fontWeight: 600 }}>
                  Not mapped
                </Typography>
              )}
            </Stack>
          </Box>
        </Stack>
      ),
    },
    {
      field: "gstNo",
      headerName: "Compliance",
      width: 170,
      sortable: false,
      renderCell: ({ row }) => (
        <Stack spacing={0.3} sx={{ py: 0.7 }}>
          <Typography sx={{ fontSize: "0.72rem", color: row.gstNo ? T.ink : T.subtle, fontFamily: "monospace" }}>
            GST {row.gstNo || "—"}
          </Typography>
          <Typography sx={{ fontSize: "0.72rem", color: row.medicalLicenseNo ? T.ink : T.subtle, fontFamily: "monospace" }}>
            LIC {row.medicalLicenseNo || "—"}
          </Typography>
          {row.bussinessRegNo && (
            <Typography sx={{ ...mono, fontSize: "0.68rem" }}>
              REG {row.bussinessRegNo}
            </Typography>
          )}
        </Stack>
      ),
    },
    {
      field: "dateOfRegistration",
      headerName: "Dates",
      width: 160,
      sortable: false,
      renderCell: ({ row }) => (
        <Stack spacing={0.3} sx={{ py: 0.7 }}>
          <Typography sx={{ fontSize: "0.72rem", color: T.muted }}>
            Joined {day(row.joinedAt) || "—"}
          </Typography>
          <Typography sx={{ fontSize: "0.72rem", color: T.muted }}>
            Reg {looseDate(row.dateOfRegistration) || "—"}
          </Typography>
          <Typography sx={{ fontSize: "0.72rem", color: T.muted }}>
            Lic {looseDate(row.dateOfMedicalLicense) || "—"}
          </Typography>
        </Stack>
      ),
    },
    {
      field: "businessTiming",
      headerName: "Hours",
      width: 110,
      sortable: false,
      renderCell: ({ row }) => (
        <Typography sx={{ fontSize: "0.75rem", color: hours(row.businessTiming) ? T.ink : T.subtle }}>
          {hours(row.businessTiming) || "Not set"}
        </Typography>
      ),
    },
    {
      // Approving a pharmacy is the action this screen exists for, so the
      // switch stays in the row rather than moving into the console.
      field: "verifiedPharamacy",
      headerName: "Approved",
      width: 120,
      sortable: false,
      renderCell: ({ row }) => (
        <Stack alignItems="center" spacing={0.2} sx={{ py: 0.7 }} onClick={(e) => e.stopPropagation()}>
          <Switch
            size="small"
            checked={Boolean(row.verifiedPharamacy)}
            disabled={row.isDeleted || busyId === row.pharmacyId}
            onChange={(e) => onToggleVerified(row, e.target.checked)}
          />
          <Typography
            sx={{
              fontSize: "0.65rem",
              fontWeight: 700,
              letterSpacing: "0.04em",
              textTransform: "uppercase",
              color: row.verifiedPharamacy ? T.success : T.warning,
            }}
          >
            {row.verifiedPharamacy ? "Verified" : "Pending"}
          </Typography>
        </Stack>
      ),
    },
    {
      field: "status",
      headerName: "State",
      width: 150,
      sortable: false,
      renderCell: ({ row }) => (
        <Stack spacing={0.4} sx={{ py: 0.7 }} alignItems="flex-start">
          {row.isDeleted && <StatusChip status="cancelled" label="Deleted" />}
          <StatusChip
            status={row.isTakingOrders ? "accepted" : "pending"}
            label={row.isTakingOrders ? "Taking orders" : "Paused"}
          />
          <Stack direction="row" spacing={0.5}>
            {row.isLoggedIn && <StatusChip status="active" label="Online" />}
            {row.posConnected && (
              <StatusChip status="confirmed" label={row.syncPosKind || "POS"} />
            )}
          </Stack>
        </Stack>
      ),
    },
    {
      field: "bankStatus",
      headerName: "Bank",
      width: 150,
      sortable: false,
      renderCell: ({ row }) => {
        // Neutral rather than red: most pharmacies add bank details when they
        // first want paying, so "no bank" is a stage, not a fault.
        if (!row.hasBankDetails) {
          return <StatusChip tone={T.subtle} label="No bank" />;
        }
        // A near name-match is a human decision, and the person who has to
        // make it needs to see that it is waiting.
        if (row.bankReviewRequired) {
          return <StatusChip tone={T.warning} label="Needs review" />;
        }
        return (
          <Stack spacing={0.3} sx={{ py: 0.7 }} alignItems="flex-start">
            <StatusChip status={row.bankStatus} label={BANK_LABELS[row.bankStatus] || row.bankStatus} />
            {row.bankName && (
              <Typography sx={{ ...mono, fontSize: "0.65rem" }}>{row.bankName}</Typography>
            )}
          </Stack>
        );
      },
    },
    {
      field: "actions",
      headerName: "",
      width: 190,
      sortable: false,
      filterable: false,
      // Clicking a row opens the console, so nothing in this cell may bubble.
      renderCell: ({ row }) => (
        <Stack direction="row" spacing={0.3} onClick={(e) => e.stopPropagation()}>
          <Tooltip title="Open pharmacy console">
            <IconButton size="small" onClick={() => onOpenConsole(row)} sx={{ color: T.primary }}>
              <MdOpenInNew size={16} />
            </IconButton>
          </Tooltip>

          <Tooltip title="Manage inventory">
            <IconButton size="small" onClick={() => onOpenInventory(row)} sx={{ color: T.secondary }}>
              <MdInventory2 size={16} />
            </IconButton>
          </Tooltip>

          <Tooltip title={row.isDeleted ? "Editing a deleted pharmacy is disabled" : "Edit details"}>
            {/* A disabled IconButton fires no events, so the Tooltip needs a
                wrapper that still does. */}
            <span>
              <IconButton
                size="small"
                disabled={row.isDeleted}
                onClick={() => onEdit(row)}
                sx={{ color: T.muted }}
              >
                <MdEdit size={16} />
              </IconButton>
            </span>
          </Tooltip>

          {row.isDeleted ? (
            <Tooltip title="Restore pharmacy">
              <IconButton size="small" onClick={() => onRestore(row)} sx={{ color: T.success }}>
                <MdRestoreFromTrash size={16} />
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip title="Delete pharmacy">
              <IconButton size="small" onClick={() => onDelete(row)} sx={{ color: T.error }}>
                <MdDelete size={16} />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      ),
    },
  ];
}

export { TwoLine };
